import {
    Component,
    ElementRef,
    HostListener,
    inject,
    input,
    NgZone,
    OnDestroy,
    OnInit,
    output,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    CellClickedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    RowClickedEvent,
    SelectionChangedEvent
} from 'ag-grid-community';
import { AgGridAngular, AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { NgClass, NgStyle } from '@angular/common';

@Component({
    selector: 'aix-data-grid',
    templateUrl: './aix-data-grid.component.html',
    styleUrls: ['./aix-data-grid.component.scss'],
    /**
     * Note: ViewEncapsulation specified because we are styling the underlying
     * ag-grid
     */
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [AgGridModule, NgClass, NgStyle]
})
export class AixDataGridComponent implements OnInit, OnDestroy {
    private hostRef = inject(ElementRef);
    private zone = inject(NgZone);

    agGrid = viewChild<AgGridAngular>('agGrid');

    frameworkComponents = input<{ [key: string]: new () => ICellRendererAngularComp }>();
    appearance = input<string>();
    domLayout = input<'normal' | 'autoHeight' | 'print' | undefined>();
    noRowsTemplate = input<string>();
    style = input<{ [key: string]: any }>();
    sortable = input(false);
    fitColumns = input(true);
    noHoverStyle = input(false);
    showHeader = input(false);
    rowData = input<any[]>();
    gridOptions = input<GridOptions>();
    rowSelection = input<'single' | 'multiple'>();
    // Typed explicitly: `input(null)` infers the type as literally `null`, so no
    // caller could pass the context object ag-grid cell renderers read from.
    context = input<any>(null);

    gridReady = output<GridReadyEvent>();
    rowClicked = output<RowClickedEvent>();
    cellClicked = output<CellClickedEvent>();
    selectionChanged = output<SelectionChangedEvent>();

    mergedGridOpts: GridOptions;
    defaultGridOpts: GridOptions;

    gridApi: GridApi | undefined;
    paginationPageSize = 9;
    headerHeight: number;
    rowHeight: number;

    private resizeObserver: ResizeObserver | undefined;
    private lastObservedWidth = 0;

    constructor() {}

    ngOnInit() {
        this.setupGrid();
        this.observeContainerWidth();
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
    }

    /**
     * Columns are only re-fitted on first data render and on window resize, so a grid
     * whose *container* changes width without the window changing keeps the widths it
     * was first laid out with -- rows then overflow their column, with a horizontal
     * scrollbar, until something resizes the window.
     *
     * The common way in is a sidebar or panel that renders after the grid does, because
     * whichever response lands first is a race: same page, same data, correct one visit
     * and wrong the next.
     */
    private observeContainerWidth() {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        // Observed outside Angular: this fires on every layout change of the host,
        // including the height changes an autoHeight grid makes on its own, and a
        // change-detection pass for each of those would be pure churn.
        this.zone.runOutsideAngular(() => {
            this.resizeObserver = new ResizeObserver(() => {
                const width = this.hostRef.nativeElement.clientWidth;

                // Width 0 means hidden, not narrow: fitting to it would collapse every
                // column to its minimum, and the widths would still be wrong on return.
                if (!width || width === this.lastObservedWidth) {
                    return;
                }

                this.lastObservedWidth = width;

                // Back inside for the re-fit itself, so this path behaves exactly like
                // the window:resize one above it. Only the observation is cheap and
                // frequent; a width change is rare, and leaving it outside would mean
                // anything downstream of the resulting columnResized that needs a pass
                // works on a window resize and silently does not on a container one.
                this.zone.run(() => this.onResize());
            });
            this.resizeObserver.observe(this.hostRef.nativeElement);
        });
    }

    setupGrid() {
        // NB> headerHeight and rowHeight need to match the equivalent CSS variables
        this.headerHeight = 56;
        this.rowHeight = 56;

        const columnDefs = this.gridOptions()?.columnDefs;

        this.defaultGridOpts = {
            columnDefs: columnDefs,
            rowClass: this.noHoverStyle() ? 'no-hover' : '',
            rowHeight: this.rowHeight,
            headerHeight: this.headerHeight,
            rowSelection: this.rowSelection(),
            suppressCellFocus: true,
            suppressRowHoverHighlight: this.noHoverStyle(),
            suppressMovableColumns: true,
            pagination: true,
            paginationPageSize: this.paginationPageSize,
            suppressPaginationPanel: true,
            suppressRowClickSelection: true,
            maxConcurrentDatasourceRequests: 1,
            maxBlocksInCache: 50,
            cacheBlockSize: 25,
            overlayLoadingTemplate: ' ',
            overlayNoRowsTemplate:
                '<p class="u-mt16 u-mb32">No records found, please try a different search term or filter.</p>',
            onGridReady: this.onGridReady.bind(this),
            onRowClicked: this.onRowClicked.bind(this),
            onCellClicked: this.onCellClicked.bind(this),
            onSelectionChanged: this.onSelectionChanged.bind(this),
            onFirstDataRendered: this.onResize.bind(this)
        };

        this.mergedGridOpts = { ...this.defaultGridOpts, ...this.gridOptions() };
    }

    search(searchText: string) {
        this.gridApi?.setGridOption('quickFilterText', searchText);
        if (this.gridApi?.getDisplayedRowCount() === 0) {
            this.gridApi.showNoRowsOverlay();
        } else {
            this.gridApi?.hideOverlay();
        }
    }

    onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridReady.emit(event);
    }

    @HostListener('window:resize')
    onResize() {
        if (this.fitColumns()) {
            setTimeout(() => {
                this.gridApi?.sizeColumnsToFit();
            });
        }
    }

    onRowClicked(event: RowClickedEvent) {
        if (!this.noHoverStyle()) {
            this.rowClicked.emit(event);
        }
    }

    onCellClicked(event: CellClickedEvent) {
        if (!this.noHoverStyle()) {
            this.cellClicked.emit(event);
        }
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        this.selectionChanged.emit(event);
    }
}
